/**
 * Helpers for "Kind C" plain-date fields — e.g. birth dates, invoice
 * period boundaries, promo campaign validity dates. These correspond to
 * `LocalDate` on the backend and ride the wire as `"YYYY-MM-DD"` strings
 * with no time or offset.
 *
 * The JS `Date` parser is extremely hostile to these: `new Date("1990-05-15")`
 * coerces the string to **UTC midnight**, so reading it back with
 * `getDate()` / `getMonth()` on a browser east of UTC shifts it by a day.
 * Similarly `date.toISOString().slice(0, 10)` pulls numerals out of the
 * UTC projection, not the local one, so writing is also buggy west of UTC.
 *
 * These helpers never touch `toISOString()` or `Date.parse(iso)`. They
 * extract local numerals only, which is what Kind C semantics require.
 *
 * Reach for {@link parseLocalDate} + {@link formatLocalDate} everywhere a
 * birth-date-like field enters or leaves a component. Never `new Date(x)`.
 */

/** Structural representation of a calendar date, no time component. */
export interface LocalDateParts {
  year: number;
  /** 1-indexed (1 = January). */
  month: number;
  /** 1-indexed day of month. */
  day: number;
}

/**
 * Parse a `YYYY-MM-DD` string into its calendar components. Returns
 * `null` for missing / malformed input. Only the first 10 characters
 * are inspected, so `"1990-05-15T00:00:00Z"` also parses (the time /
 * offset are ignored — the caller has already asked for a plain date).
 */
export function parseLocalDateParts(
  str: string | null | undefined,
): LocalDateParts | null {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return {year, month, day};
}

/**
 * Parse a `YYYY-MM-DD` string into a `Date` pinned to **browser-local
 * midnight** for that calendar day. This is what form inputs like
 * `ut-date-input` expect — they read `.getDate()` etc. on the local
 * timezone, so a UTC-midnight `Date` from `new Date(str)` would render
 * as the wrong day for any user west of UTC.
 */
export function parseLocalDate(
  str: string | null | undefined,
): Date | null {
  const parts = parseLocalDateParts(str);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

/**
 * Format a `Date` back into a `YYYY-MM-DD` string by reading its
 * **local** year/month/day numerals. Inverse of {@link parseLocalDate}.
 *
 * Do NOT use `date.toISOString().slice(0, 10)` for this — that pulls
 * the numerals out of the UTC projection, which shifts by a day for
 * any user east of UTC.
 */
export function formatLocalDate(
  date: Date | null | undefined,
): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Given a UTC instant ISO string (e.g. `"2026-04-15T14:00:00Z"`), return
 * the `YYYY-MM-DD` of the **browser-local** day that instant falls on.
 * Used to bucket events-by-day / filter events-by-date in the UI:
 * pass the backend's `startDateTimeInstant` here and you get the
 * viewer's local calendar day, never shifted by a UTC rounding trip.
 */
export function instantToLocalDateString(
  instant: string | null | undefined,
): string | null {
  if (!instant) return null;
  return formatLocalDate(new Date(instant));
}
